"use client"

import { useEffect, useRef } from "react"
import { useSearchParams } from "next/navigation"
import { useDiscountStore } from "@/lib/stores/discount"
import { useToast } from "@/components/ui/use-toast"
import { axiosInstance } from "@/lib/axios"
import type { IUtmParams } from "@/lib/types/referral"
import { useReferralStore } from "@/lib/stores/referralStore"
import { useTrackReferral } from "@/lib/services/api"

export default function DiscountTracker() {
  const searchParams = useSearchParams()
  const { setDiscountCode, setDiscountData } = useDiscountStore()
  const { toast } = useToast()
  const { setRef } = useReferralStore()
  const trackReferralMutation = useTrackReferral()
  const processedRef = useRef<string | null>(null)
  useEffect(() => {
    const checkDiscount = async (code: string) => {
      try {
        const response = await axiosInstance.post("/api/discount/check", {
          ma_giam_gia: code,
          total_amount: 0, // Initial check without cart total
          cart_items: [] // Empty cart items for initial validation
        })

        if (response.data.success && response.data.data) {
          setDiscountCode(code)
          setDiscountData(response.data.data)
          toast({
            title: "Mã giảm giá hợp lệ",
            description: response.data.message,
          })
        } else {
          toast({
            variant: "destructive",
            title: response.data.message || "Mã giảm giá không hợp lệ",
            description: response.data.message,
          })
        }
      } catch (error: any) {
        console.log(error.response?.data?.message)
        
        // Check if this is a validation error (400) with a proper message
        if (error?.response?.status === 400 && error?.response?.data?.message) {
          const errorData = error.response.data
          const courseName = errorData.course ? ` (${errorData.course})` : ''
          
          toast({
            variant: "destructive",
            title: "Mã giảm giá không hợp lệ",
            description: `${errorData.message}${courseName}`
          })
        } else {
          // This is a real system error
          toast({
            variant: "destructive",
            title: "Lỗi hệ thống",
            description: error?.response?.data?.message || "Không thể kiểm tra mã giảm giá. Vui lòng thử lại.",
          })
        }
      }
    }

    const discount = searchParams.get("discountCode")
    if (discount) {
      checkDiscount(discount)
    }
  }, [searchParams, setDiscountCode, setDiscountData, toast])

  useEffect(() => {
    const ref = searchParams.get("discountCode")
    if (ref && ref !== processedRef.current) {
      processedRef.current = ref
      setRef(ref)

      // Track the referral click using the service
      const utmParams: IUtmParams = {
        utm_source: searchParams.get("utm_source") || undefined,
        utm_medium: searchParams.get("utm_medium") || undefined,
        utm_campaign: searchParams.get("utm_campaign") || undefined,
        utm_term: searchParams.get("utm_term") || undefined,
        utm_content: searchParams.get("utm_content") || undefined,
      }

      // Filter out undefined values
      const filteredUtmParams = Object.fromEntries(
        Object.entries(utmParams).filter(([_, value]) => value !== undefined)
      ) as IUtmParams

      trackReferralMutation.mutate({
        code: ref,
        utmParams: Object.keys(filteredUtmParams).length > 0 ? filteredUtmParams : undefined
      })
    }
  }, [searchParams, setRef])

  return null
} 